import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';
import moment from 'moment';

import {
  getUserAvailabilityState,
  TUpdateuserAvailabilityAsAdmin,
  TUserAvailabilitySlot,
  updateUserAvialabilityAsAdmin,
} from '../../../../../store/V2/UserAvailabilitySlice';
import { useAppSelector } from '../../../../../hooks';
import { TPTextField } from '../../../../../components/Inputs';

interface AvailabilityModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
}

type TMenuData = {
  open: boolean;
  anchorEl: HTMLElement;
  day: string;
  selectedDays: string[];
};

type TSlotItem = {
  start: string; // HH:mm (24hour format)
  end: string; // HH:mm (24hour format)
};

type TDayProps = {
  checked: boolean;
  slots: TSlotItem[];
};

type TAvailabilityState = {
  Sunday: TDayProps;
  Monday: TDayProps;
  Tuesday: TDayProps;
  Wednesday: TDayProps;
  Thursday: TDayProps;
  Friday: TDayProps;
  Saturday: TDayProps;
};

const defaultSlotItem = { start: '09:00', end: '10:00' };

const initialAvailabilityState: TAvailabilityState = {
  Sunday: {
    checked: false,
    slots: [],
  },
  Monday: {
    checked: false,
    slots: [],
  },
  Tuesday: {
    checked: false,
    slots: [],
  },
  Wednesday: {
    checked: false,
    slots: [],
  },
  Thursday: {
    checked: false,
    slots: [],
  },
  Friday: {
    checked: false,
    slots: [],
  },
  Saturday: {
    checked: false,
    slots: [],
  },
};

const AvailabilityModal: FunctionComponent<AvailabilityModalProps> = ({
  open,
  onClose,
  userId,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const userAvailabilityState = useAppSelector(getUserAvailabilityState);

  // State
  const [availabilityState, setAvailabilityState] = useState(
    initialAvailabilityState
  );

  const [menuData, setMenuData] = useState<TMenuData | undefined>(undefined);
  const [isLoading, setLoading] = useState(false);

  const { adminUserAvailability } = userAvailabilityState;
  const allDays = Object.keys(availabilityState);

  useEffect(() => {
    if (adminUserAvailability && adminUserAvailability.slots) {
      setAvailabilityState((prevState) => {
        const newState = JSON.parse(JSON.stringify({ ...prevState }));
        const utcDays = Object.keys(adminUserAvailability.slots);

        utcDays.forEach((utcDay) => {
          const matchingSlots: TUserAvailabilitySlot[] =
            adminUserAvailability.slots[utcDay] || [];

          matchingSlots.forEach((matchingSlot) => {
            const utcDate = moment
              .utc()
              .day(utcDay)
              .set({
                hour: parseInt(matchingSlot.start.split(':')[0], 10),
                minute: parseInt(matchingSlot.start.split(':')[1], 10),
              });

            const date = utcDate.clone().local();
            const dayName = date.format('dddd');
            const start = date.format('HH:mm');
            const end = moment
              .utc()
              .day(utcDay)
              .set({
                hour: parseInt(matchingSlot.end.split(':')[0], 10),
                minute: parseInt(matchingSlot.end.split(':')[1], 10),
              })
              .local()
              .format('HH:mm');

            newState[dayName].checked = true;
            newState[dayName].slots.push({
              start,
              end,
            });
          });
        });

        return newState;
      });
    }
  }, []);

  const toggleDaySwitch = (day, checked) => {
    setAvailabilityState((prevState) => {
      const newState: TAvailabilityState = { ...prevState };

      if (checked) {
        newState[day] = {
          checked: true,
          slots: newState[day].slots.length
            ? [...newState[day].slots]
            : [defaultSlotItem],
        };
      } else {
        newState[day].checked = false;
      }

      return newState;
    });
  };

  const handleSlotChange = (
    day: string,
    slotIndex: number,
    value: string,
    selector: 'start' | 'end'
  ) => {
    setAvailabilityState((prevState) => {
      const newState: TAvailabilityState = JSON.parse(
        JSON.stringify({ ...prevState })
      );

      newState[day].slots[slotIndex][selector] = value;

      return newState;
    });
  };

  const addNewSlot = (day: string) => {
    setAvailabilityState((prevState) => {
      const format = 'HH:mm';
      const newState: TAvailabilityState = { ...prevState };

      const selectedDayData: TDayProps = newState[day];

      if (selectedDayData.slots && selectedDayData.slots.length) {
        const lastIndex = selectedDayData.slots.length - 1;
        const nextStart = moment(selectedDayData.slots[lastIndex].end, format);

        newState[day] = {
          ...newState[day],
          slots: [
            ...newState[day].slots,
            {
              start: nextStart.format(format),
              end: nextStart.clone().add(1, 'hour').format(format),
            },
          ],
        };
      }

      return newState;
    });
  };

  const deleteSlot = (day: string, slotIndex: number) => {
    setAvailabilityState((prevState) => {
      const newState: TAvailabilityState = { ...prevState };

      const newDaySlots = newState[day].slots.filter(
        (item, index) => index !== slotIndex
      );

      newState[day] = {
        ...newState[day],
        slots: newDaySlots,
      };

      return newState;
    });
  };

  const handleMenuSelectedDays = (day: string) => {
    setMenuData((prevState) => {
      if (prevState) {
        const newState = { ...prevState };

        let newSelectedDays = newState.selectedDays || [];

        if (newSelectedDays.includes(day)) {
          newSelectedDays = newSelectedDays.filter((item) => item !== day);
        } else {
          newSelectedDays.push(day);
        }

        newState.selectedDays = newSelectedDays;

        return newState;
      }

      return prevState;
    });
  };

  const copySlots = () => {
    setAvailabilityState((prevState) => {
      const newState = { ...prevState };

      if (menuData) {
        const slotsToBeCopied = [...newState[menuData.day].slots];

        menuData.selectedDays.forEach((day) => {
          newState[day] = {
            checked: true,
            slots: slotsToBeCopied,
          };
        });
      }

      return newState;
    });

    closeMenu();
  };

  const openMenu = (element: HTMLElement, day: string) => {
    setMenuData({
      open: true,
      anchorEl: element,
      day,
      selectedDays: [],
    });
  };

  const closeMenu = () => {
    setMenuData(undefined);
  };

  const handleSubmit = async () => {
    setLoading(true);

    const apiData: TUpdateuserAvailabilityAsAdmin = {
      userId,
      slots: {
        Sunday: [],
        Monday: [],
        Tuesday: [],
        Wednesday: [],
        Thursday: [],
        Friday: [],
        Saturday: [],
      },
    };

    const days = Object.keys(availabilityState);

    days.forEach((day) => {
      const matchingItem: TDayProps = availabilityState[day];

      if (
        matchingItem &&
        matchingItem.checked &&
        matchingItem.slots &&
        matchingItem.slots.length
      ) {
        matchingItem.slots.forEach((slot) => {
          const date = moment(slot.start, 'HH:mm').day(day);
          const utcDate = date.clone().utc();
          const utcDayName = utcDate.format('dddd');
          const start = utcDate.format('HH:mm');
          const end = moment(slot.end, 'HH:mm').day(day).utc().format('HH:mm');

          apiData.slots[utcDayName].push({
            start,
            end,
          });
        });
      }
    });

    await dispatch(updateUserAvialabilityAsAdmin(apiData));

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: '700px',
          padding: isXsScreen ? '15px' : '30px',
          borderRadius: '20px',
        },
      }}
    >
      <Stack direction="row" alignItems="center" mb="20px">
        <DialogTitle
          sx={{
            p: 0,
            color: '#000000',
            fontWeight: 600,
            fontSize: '16px',
            flexGrow: 1,
          }}
        >
          Set your availability
        </DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon sx={{ color: '#000000' }} />
        </IconButton>
      </Stack>
      <div>
        <Stack style={!isXsScreen ? { padding: '16px' } : undefined}>
          <Grid container spacing={2}>
            {allDays.map((day) => {
              const dayState: TDayProps = availabilityState[day];
              const daySlots = dayState.checked ? dayState.slots : [];

              if (isXsScreen) {
                return (
                  <Grid key={day} item xs={12}>
                    <Stack flexGrow={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <div style={{ minWidth: '150px' }}>
                          <FormControlLabel
                            control={<Switch checked={dayState.checked} />}
                            label={day}
                            onChange={(event, checked) =>
                              toggleDaySwitch(day, checked)
                            }
                          />
                        </div>
                        {dayState.checked ? (
                          <Stack direction="row" alignItems="center">
                            <Tooltip title="Add new time slot" placement="top">
                              <IconButton onClick={() => addNewSlot(day)}>
                                <AddIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy times to..." placement="top">
                              <IconButton
                                id={`${day}-btn-copy`}
                                onClick={(event) =>
                                  openMenu(event.currentTarget, day)
                                }
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : null}
                      </Stack>
                      <Stack spacing={2}>
                        {daySlots.map((slotItem, slotIndex) => {
                          const slotKey = `${day}-${slotIndex}`;

                          return (
                            <Stack
                              key={slotKey}
                              gap={0}
                              direction="row"
                              alignItems="center"
                            >
                              <div>
                                <TPTextField
                                  id={`${slotKey}-start`}
                                  size="small"
                                  type="time"
                                  value={slotItem.start}
                                  onChange={(e) =>
                                    handleSlotChange(
                                      day,
                                      slotIndex,
                                      e.target.value,
                                      'start'
                                    )
                                  }
                                  InputProps={{
                                    sx: { fontSize: '12px', minWidth: 'unset' },
                                  }}
                                />
                              </div>
                              <Typography variant="h6" m="0 10px">
                                -
                              </Typography>
                              <div>
                                <TPTextField
                                  id={`${slotKey}-end`}
                                  size="small"
                                  type="time"
                                  value={slotItem.end}
                                  onChange={(e) =>
                                    handleSlotChange(
                                      day,
                                      slotIndex,
                                      e.target.value,
                                      'end'
                                    )
                                  }
                                  InputProps={{
                                    sx: { fontSize: '12px', minWidth: 'unset' },
                                  }}
                                />
                              </div>
                              <IconButton
                                onClick={() => deleteSlot(day, slotIndex)}
                              >
                                <DeleteIcon color="error" />
                              </IconButton>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Stack>
                  </Grid>
                );
              }

              return (
                <Grid key={day} xs={12}>
                  <Stack direction="row" gap={2} mb="15px">
                    <div style={{ minWidth: '150px' }}>
                      <FormControlLabel
                        control={<Switch checked={dayState.checked} />}
                        label={day}
                        onChange={(event, checked) =>
                          toggleDaySwitch(day, checked)
                        }
                      />
                    </div>
                    <Stack gap={2}>
                      {daySlots.map((daySlot, daySlotIndex) => {
                        const isFirstItem = daySlotIndex === 0;
                        const slotKey = `${day}-${daySlotIndex}`;

                        return (
                          <Stack
                            key={slotKey}
                            gap={2}
                            direction="row"
                            alignItems="center"
                          >
                            <div>
                              <TPTextField
                                id={`${slotKey}-start`}
                                size="small"
                                type="time"
                                value={daySlot.start}
                                onChange={(e) =>
                                  handleSlotChange(
                                    day,
                                    daySlotIndex,
                                    e.target.value,
                                    'start'
                                  )
                                }
                              />
                            </div>
                            <Typography variant="h6">-</Typography>
                            <div>
                              <TPTextField
                                id={`${slotKey}-end`}
                                size="small"
                                type="time"
                                value={daySlot.end}
                                onChange={(e) =>
                                  handleSlotChange(
                                    day,
                                    daySlotIndex,
                                    e.target.value,
                                    'end'
                                  )
                                }
                              />
                            </div>
                            {isFirstItem ? (
                              <>
                                <Tooltip
                                  title="Add new time slot"
                                  placement="top"
                                >
                                  <IconButton onClick={() => addNewSlot(day)}>
                                    <AddIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title="Copy times to..."
                                  placement="top"
                                >
                                  <IconButton
                                    id={`${day}-btn-copy`}
                                    onClick={(event) =>
                                      openMenu(event.currentTarget, day)
                                    }
                                  >
                                    <ContentCopyIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip title="Delete" placement="top">
                                <IconButton
                                  onClick={() => deleteSlot(day, daySlotIndex)}
                                >
                                  <DeleteIcon color="error" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </Stack>
        {/* MODALS */}
        {menuData ? (
          <Menu
            anchorEl={menuData.anchorEl}
            open={menuData.open}
            onClose={closeMenu}
            PaperProps={{
              sx: { borderRadius: '7px' },
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ p: '0 16px', mb: '15px' }}
            >
              COPY TIMES TO
            </Typography>
            {allDays.map((day, index) => {
              const isFromDay = day === menuData.day;
              const isSelected =
                isFromDay || menuData.selectedDays.includes(day);

              return (
                <MenuItem
                  key={`li-${day}`}
                  disabled={isFromDay}
                  onClick={() => handleMenuSelectedDays(day)}
                >
                  <ListItemText>{day}</ListItemText>
                  <ListItemIcon>
                    {isSelected ? (
                      <CheckBoxIcon color={isFromDay ? 'primary' : undefined} />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )}
                  </ListItemIcon>
                </MenuItem>
              );
            })}
            <Divider />
            <Button
              fullWidth
              color="primary"
              sx={{ borderRadius: 0 }}
              onClick={copySlots}
              disabled={!menuData.selectedDays.length}
            >
              Apply
            </Button>
          </Menu>
        ) : null}
      </div>
      <Stack direction="row" alignItems="center" justifyContent="flex-end">
        <LoadingButton
          loading={isLoading}
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Save
        </LoadingButton>
      </Stack>
    </Dialog>
  );
};

export default AvailabilityModal;
